import React, { useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Search, Globe, Phone, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useReducedMotion } from "@/hooks/useReducedMotion";

export default function Outlets({ outlets = [] }) {
  const [q, setQ] = useState("");
  const [province, setProvince] = useState("");
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], reduced ? ["0%", "0%"] : ["-12%", "12%"]);

  const provinces = useMemo(() => [...new Set(outlets.map((o) => o.province).filter(Boolean))].sort(), [outlets]);
  const filtered = outlets.filter((o) => {
    const matchQ = !q || [o.name, o.city, o.address].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()));
    const matchP = !province || o.province === province;
    return matchQ && matchP;
  });

  return (
    <section id="verkooppunten" ref={ref} className="relative overflow-hidden bg-sfc-night px-5 py-28 lg:px-10">
      {/* Parallax-achtergrond: SoulFoodCity skyline-banner, cover + gecentreerd */}
      <motion.div style={{ y: yBg }} className="absolute inset-x-0 -top-[15%] -bottom-[15%] z-0" aria-hidden="true">
        <img src="/soulfoodcity-banner.jpg" alt="" className="h-full w-full object-cover object-center" />
      </motion.div>
      {/* Overlay voor leesbaarheid van de kaarten (licht genoeg om de banner te tonen) */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-sfc-night/85 via-sfc-night/40 to-sfc-night/90" />

      <div className="relative z-10 mx-auto max-w-7xl">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-sfc-yellow">Verkooppunten</span>
          <h2 className="mt-4 font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Vind Soul Food City bij jou in de buurt
          </h2>
        </Reveal>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="outlet-search"
              placeholder="Zoek op naam, plaats of adres..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full rounded-full border border-white/10 bg-sfc-night/60 backdrop-blur-sm py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-white/40 focus:border-sfc-orange focus:outline-none"
            />
          </div>
          <select
            data-testid="outlet-province-filter"
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            className="rounded-full border border-white/10 bg-sfc-night/60 backdrop-blur-sm px-5 py-3.5 text-sm text-white focus:border-sfc-orange focus:outline-none"
          >
            <option value="">Alle provincies</option>
            {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {filtered.length === 0 && <p className="text-white/50">Geen verkooppunten gevonden.</p>}
          {filtered.map((o) => (
            <article key={o.id} data-testid={`outlet-card-${o.id}`} className="rounded-3xl border border-white/10 bg-sfc-night/70 backdrop-blur-md p-6 transition-colors hover:border-sfc-orange/40">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-display text-lg text-white">{o.name}</h3>
                {o.is_featured && <span className="rounded-full bg-sfc-orange/20 px-2.5 py-1 text-xs text-sfc-orange">Uitgelicht</span>}
              </div>
              <p className="mt-3 flex items-start gap-2 text-sm text-white/70"><MapPin size={16} className="mt-0.5 shrink-0 text-sfc-cyan" />{o.address}, {o.postal_code} {o.city}</p>
              {o.opening_hours && <p className="mt-2 flex items-center gap-2 text-sm text-white/60"><Clock size={16} className="text-sfc-cyan" />{o.opening_hours}</p>}
              {o.phone && <p className="mt-2 flex items-center gap-2 text-sm text-white/60"><Phone size={16} className="text-sfc-cyan" />{o.phone}</p>}
              {o.website && <a href={o.website} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center gap-2 text-sm text-sfc-orange hover:underline"><Globe size={16} />Website</a>}
              {o.products?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {o.products.map((p) => <span key={p} className="rounded-full border border-white/10 px-2.5 py-1 text-xs text-white/60">{p}</span>)}
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
